import React, { FC, memo, useEffect, useMemo, useState } from 'react';

import { findAgendaInregelingDetails } from '@/apps/mwd-v2/components/MwdTooltip/agendaInregelingNotitie';
import DetailPanelOnPrognoseTooltipContent from '@/apps/mwd-v2/components/MwdTooltip/DetailPanelOnPrognoseTooltipContent';
import DetailPanelOnSpoorTooltipContent from '@/apps/mwd-v2/components/MwdTooltip/DetailPanelOnSpoorTooltipContent';
import DetailPanelOnTimesTooltipContent from '@/apps/mwd-v2/components/MwdTooltip/DetailPanelOnTimesTooltipContent';
import LegendRightTooltipContent from '@/apps/mwd-v2/components/MwdTooltip/LegendRightTooltipContent';
import MwdOvergangTooltipContent from '@/apps/mwd-v2/components/MwdTooltip/MwdOvergangTooltipContent';
import PerronLengteConflictTooltipContent from '@/apps/mwd-v2/components/MwdTooltip/PerronLengteConflictTooltipContent';
import { TooltipDetails } from '@/apps/mwd-v2/state/types/TooltipStore';
import { useHasPermission } from '@/auth';
import useGetAgendaApi from '@/hooks/useGetAgendaApi';
import useGetEenhedenInregelingStatus from '@/hooks/useGetEenhedenInregelingStatus';
import { Overgang } from '@/types/deeltrajecten';
import { envVariables } from '@/utils/env';
import { defaultStyles, TooltipWithBounds } from '@visx/tooltip';

import useWerklijnen from '../../hooks/useWerklijnen';
import { TOOLTIP_SHOW_HIDE_TIME } from '../../state/actions/tooltip';
import useTooltipActions from '../../state/actions/useTooltipActions';
import useTooltipStore from '../../state/selectors/useTooltipStore';
import { WerklijnLegendLeft, WerklijnLegendRight } from '../../types/legends';

import TooltipVeiligheidsConflicten from './parts/TooltipVeiligheidsConflicten';
import LegendLeftTooltipContent from './LegendLeftTooltipContent';
import MwdTooltipContent from './MwdTooltipContent';

import styles from './MwdTooltip.module.css';

const DeeltrajectTooltipContent = memo(MwdTooltipContent);
const VeiligheidsConflicten = memo(TooltipVeiligheidsConflicten);
const LeftTooltipContent = memo(LegendLeftTooltipContent);
const RightTooltipContent = memo(LegendRightTooltipContent);
const DetailOnTimesTooltipContent = memo(DetailPanelOnTimesTooltipContent);
const DetailOnSpoorTooltipContent = memo(DetailPanelOnSpoorTooltipContent);
const DetailOnPrognoseTooltipContent = memo(DetailPanelOnPrognoseTooltipContent);
const OvergangTooltipContent = memo(MwdOvergangTooltipContent);
const LeftTooltipVeiligheidTooltipContent = memo(PerronLengteConflictTooltipContent);
const tooltipStyles: React.CSSProperties = {
  ...defaultStyles,
  backgroundColor: 'var(--tractie-color-background-default-inverse)',
  color: 'var(--tractie-color-content-default-inverse)',
  minWidth: '120px',
  zIndex: 999,
  whiteSpace: 'nowrap',
  position: 'absolute',
  padding: '12px',
  pointerEvents: 'auto', // Default none voor visx tooltip en wordt benodigt voor hover van de tooltip
};
type TooltipPosition = {
  offsetTop: number;
  offsetLeft: number;
  left: number;
  top: number;
  orientation: 'top' | 'bottom' | 'left' | 'right';
};
const defaultLocalState = (
  top: number,
  left: number,
  orientation: 'top' | 'bottom' | 'left' | 'right',
): TooltipPosition => ({
  top,
  left,
  orientation,
  offsetTop: -30,
  offsetLeft: 0,
});
const totalPadding = 24;
const MwdTooltip: FC = () => {
  const { hideTooltipWithTimeout } = useTooltipActions();
  const { show, tooltipLeft, tooltipTop, data, tooltipKey, arrowPosition, tooltipTimeoutId, veiligheidsConflicten } =
    useTooltipStore();
  const leftLegend =
    tooltipKey === 'leftLegend' && data && typeof data === 'object' && 'inzettenEnClaims' in data ? data : undefined;
  // Same access rule as the Agenda route: users without Agenda access must not receive Agenda data.
  const canViewAgenda = useHasPermission('agenda.view');
  const agendaEnabled =
    canViewAgenda &&
    !!envVariables().featureAgenda &&
    show &&
    !!leftLegend?.eenheid.inregeling &&
    leftLegend.eenheid.nummer !== 'MANCO';
  const statusQuery = useGetEenhedenInregelingStatus(agendaEnabled);
  const statusAfspraakId = statusQuery.data?.find(({ nummer }) => nummer === leftLegend?.eenheid.nummer)?.inregeling
    ?.afspraakId;
  const afspraakId = typeof statusAfspraakId === 'string' && statusAfspraakId ? statusAfspraakId : undefined;
  const agendaQuery = useGetAgendaApi(agendaEnabled && !!afspraakId);

  /** The adjusted and calculated position of the tooltip */
  const [position, setPosition] = useState<TooltipPosition>(defaultLocalState(tooltipTop, tooltipLeft, arrowPosition));

  const [calculatedShow, setCalculatedShow] = useState<boolean>(false);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setCalculatedShow(show);
      if (!show) setPosition(defaultLocalState(0, 0, arrowPosition));
    }, TOOLTIP_SHOW_HIDE_TIME);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [show]);

  const { werklijnen } = useWerklijnen();
  const agendaInregelingDetails = useMemo(() => {
    if (!agendaEnabled || !leftLegend || !afspraakId) return undefined;

    return findAgendaInregelingDetails(leftLegend.eenheid.nummer, agendaQuery.data, statusQuery.data);
  }, [agendaEnabled, leftLegend, afspraakId, agendaQuery.data, statusQuery.data]);

  let agendaInregelingMelding: string | undefined;
  // Only Agenda-linked inregelingen get a message; without an afspraakId there is no note to report on.
  if (agendaEnabled && afspraakId) {
    if (agendaQuery.isError) {
      agendaInregelingMelding = 'Agendanotitie kon niet worden opgehaald.';
    } else if (agendaQuery.isPending) {
      agendaInregelingMelding = 'Agendanotitie wordt geladen...';
    } else if (agendaInregelingDetails?.privacyBlocked) {
      agendaInregelingMelding = 'Agendanotitie is afgeschermd vanwege privacy.';
    }
  }

  const conflictInfo = useMemo(() => {
    return werklijnen
      .flatMap((dt) => dt.deelTrajecten)
      .map((deeltraject) => {
        const { plaatsConflictVan, isDienstregelpuntVanInConflict, ketenConfig } = deeltraject;

        const isTurnBreak = ketenConfig?.turnKetenBreak;
        const hasPlaatsConflictVertrek = plaatsConflictVan && !isTurnBreak;
        const hasTijdConflictVertrek = isDienstregelpuntVanInConflict && !isTurnBreak;
        const hasAnyConflictVertrek = hasPlaatsConflictVertrek || hasTijdConflictVertrek;

        return {
          ...deeltraject,
          hasPlaatsConflictVertrek,
          hasTijdConflictVertrek,
          hasAnyConflictVertrek,
        };
      });
  }, [werklijnen]);

  const [tooltipContent, setTooltipContent] = useState<HTMLDivElement | null>(null);
  const [boundsVersion, setBoundsVersion] = useState(0);

  useEffect(() => {
    if (!show || !tooltipKey || !tooltipContent) return;
    const updatePosition = () => {
      const dimensions = tooltipContent.getBoundingClientRect();
      const pos = defaultLocalState(tooltipTop, tooltipLeft, arrowPosition);
      if (['top', 'bottom'].includes(arrowPosition)) {
        if (tooltipKey === 'deeltraject' || tooltipKey === 'overgang')
          pos.offsetLeft = ((dimensions.width + totalPadding) / 2) * -1;
        if (
          tooltipKey === 'leftLegend' ||
          tooltipKey === 'rightLegend' ||
          tooltipKey === 'leftLegendPerronLengteConflicten'
        ) {
          pos.offsetLeft = -30;
        }
        if (tooltipKey === 'detailPanelOnTimes') pos.offsetLeft = -34;
        if (tooltipKey === 'detailPanelOnSpoor') pos.offsetLeft = -80;
        if (tooltipKey === 'detailPanelOnPrognose') pos.offsetLeft = -150;
        // Size dependent adjustments
        if (arrowPosition === 'top' && tooltipTop + dimensions.height + totalPadding > window.innerHeight - 30) {
          pos.orientation = 'bottom';
        }

        if (arrowPosition === 'bottom' || pos.orientation === 'bottom') {
          pos.offsetTop = 84;
          pos.top -= dimensions.height + totalPadding;
          if (tooltipKey === 'detailPanelOnTimes') {
            pos.offsetLeft = -40;
          }
          if (tooltipKey === 'detailPanelOnSpoor') pos.offsetLeft = -80;
          if (tooltipKey === 'detailPanelOnPrognose') pos.offsetLeft = -150;
        }
        if (tooltipKey === 'featureFlagSearchAutocomplete') pos.offsetLeft = -140; // MaxWidth of the tooltip is 280, keep in sync with the width.
      }
      setPosition({ ...pos });
      return dimensions;
    };
    const initialDimensions = updatePosition();
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(() => {
      const { width, height } = updatePosition();
      // TooltipWithBounds only measures itself on mount, so remount it to flip within the viewport.
      if (width !== initialDimensions.width || height !== initialDimensions.height) {
        setBoundsVersion((version) => version + 1);
      }
    });
    observer.observe(tooltipContent);
    return () => observer.disconnect();
  }, [show, tooltipKey, data, arrowPosition, tooltipLeft, tooltipTop, tooltipContent]);

  return (
    <>
      {show && data && (
        <TooltipWithBounds
          key={boundsVersion}
          data-testid={`tooltip-${tooltipKey}`}
          onMouseMove={() => {
            clearTimeout(tooltipTimeoutId);
          }}
          onMouseLeave={() => {
            hideTooltipWithTimeout();
          }}
          style={{
            ...tooltipStyles,
            visibility: show && calculatedShow ? 'visible' : 'hidden',
          }}
          top={tooltipTop}
          left={tooltipLeft}
          offsetLeft={position.offsetLeft}
          offsetTop={position.offsetTop}
        >
          <div
            ref={setTooltipContent}
            className={[
              styles.mwdTooltipContent,
              ['deeltraject', 'overgang', 'featureFlagSearchAutocomplete'].includes(String(tooltipKey))
                ? styles[`mwdTooltipContent-${position.orientation}`]
                : '',
            ].join(' ')}
            style={{ height: 'auto', width: 'auto' }}
          >
            {tooltipKey === 'deeltraject' && (
              <>
                <DeeltrajectTooltipContent data={data as unknown as TooltipDetails} conflictInfo={conflictInfo} />
                <VeiligheidsConflicten conflicten={veiligheidsConflicten} />
              </>
            )}
            {tooltipKey === 'overgang' && <OvergangTooltipContent data={data as unknown as Overgang} />}
            {leftLegend && (
              <LeftTooltipContent
                arrowPosition={position.orientation}
                data={leftLegend}
                agendaInregelingNotitie={agendaInregelingMelding ? undefined : agendaInregelingDetails?.aanvraagnotitie}
                agendaInregelingMelding={agendaInregelingMelding}
                inregelingUitlooplocatie={agendaInregelingDetails?.uitlooplocatie}
              />
            )}
            {tooltipKey === 'rightLegend' && (
              <RightTooltipContent arrowPosition={position.orientation} data={data as unknown as WerklijnLegendRight} />
            )}
            {tooltipKey === 'detailPanelOnTimes' && (
              <DetailOnTimesTooltipContent arrowPosition={position.orientation} data={data as any} />
            )}
            {tooltipKey === 'detailPanelOnSpoor' && (
              <DetailOnSpoorTooltipContent arrowPosition={position.orientation} data={data as any} />
            )}
            {tooltipKey === 'detailPanelOnPrognose' && (
              <DetailOnPrognoseTooltipContent arrowPosition={position.orientation} data={data as any} />
            )}
            {tooltipKey === 'featureFlagSearchAutocomplete' && (
              <div className={styles.featureFlagSearchAutocomplete}>{data as string}</div>
            )}
            {tooltipKey === 'leftLegendPerronLengteConflicten' && (
              <LeftTooltipVeiligheidTooltipContent
                werklijnId={(data as WerklijnLegendLeft).werklijnId}
                arrowPosition={position.orientation}
                fallback={<VeiligheidsConflicten conflicten={veiligheidsConflicten} />}
              />
            )}
          </div>
        </TooltipWithBounds>
      )}
    </>
  );
};

export default MwdTooltip;
